<template>
	<div class="duty-reports-tab pa-4 bg-white">
		<!-- Report Form Dialog -->
		<v-dialog persistent max-width="600" hide-overlay v-model="showReport">
			<ReportForm
				v-if="showReport"
				:report="selectedReport"
				:selected-date="dutyDate"
				:selected-duty="selectedDuty"
				:mode="formMode"
				@cancel="cancelEdit"
				@submit="handleSubmit"
			/>
		</v-dialog>

		<AddCommentDialog :show-delete-button="true" :title="addCommentTitle" v-model="showAddCommentDialog" @delete="handleDeleteWithComment" />

		<v-snackbar :model-value="!!deleteError" @update:model-value="deleteError = null" color="error" :timeout="4000">
			{{ deleteError }}
		</v-snackbar>

		<div class="d-flex justify-space-between align-center mb-4">
			<h3 class="text-subtitle-1 font-weight-bold text-grey-darken-2">
				{{ $t('planning_extra_work') }}
			</h3>
			<v-tooltip :disabled="!isPreviewDuty && !isUnallocatedDuty" location="top" open-delay="500" max-width="150">
				<template v-slot:activator="{ props }">
					<span v-bind="props">
						<v-btn
							color="primary"
							variant="flat"
							size="small"
							@click="handleCreate"
							:disabled="reportStore.isFetching || isUnallocatedDuty || isPreviewDuty || !canEdit"
						>
							{{ $t('report.create') }}
						</v-btn>
					</span>
				</template>
				<span>
					{{ isPreviewDuty ? $t('tooltips.report_disabled_preview') : $t('report.create_disabled_unallocated') }}
				</span>
			</v-tooltip>
		</div>

		<!-- shimmer skeleton -->
		<div v-if="reportStore.isFetching">
			<div v-for="i in 3" :key="i" class="shimmer-card mb-4 rounded-lg bg-white border pa-5">
				<div class="d-flex justify-space-between align-center mb-4">
					<div class="d-flex align-center gap-3">
						<div class="shimmer-line shimmer-title"></div>
						<div class="shimmer-line shimmer-badge"></div>
					</div>
					<div class="d-flex gap-2">
						<div class="shimmer-line shimmer-icon"></div>
						<div class="shimmer-line shimmer-icon"></div>
						<div class="shimmer-line shimmer-icon"></div>
					</div>
				</div>
				<div class="d-flex gap-4 mb-4">
					<div>
						<div class="shimmer-line shimmer-label mb-1"></div>
						<div class="shimmer-line shimmer-date"></div>
					</div>
					<div>
						<div class="shimmer-line shimmer-label mb-1"></div>
						<div class="shimmer-line shimmer-date"></div>
					</div>
				</div>
				<div>
					<div class="shimmer-line shimmer-reason-label mb-1"></div>
					<div class="shimmer-line shimmer-reason-text"></div>
				</div>
			</div>
		</div>

		<div v-else-if="hasReports">
			<v-card v-for="(report, index) in reportStore.dutyReports" :key="report._id" flat class="mb-4 rounded-lg bg-grey-lighten-4">
				<div class="report-card-body">
					<div class="d-flex justify-space-between align-start mb-4">
						<div class="d-flex align-center">
							<span class="text-subtitle-1 font-weight-bold">{{ $t('report.title_with_index', { index: index + 1 }) }}</span>
							<span :class="['status-chip', 'ml-3', getStatusClass(report.status)]">
								{{ formatStatus(report.status) }}
							</span>
						</div>
						<div class="report-actions">
							<v-menu :close-on-content-click="false" location="bottom end" offset="10">
								<template v-slot:activator="{ props }">
									<v-btn v-bind="props" icon="mdi-information" variant="text" size="small" color="grey-darken-1"></v-btn>
								</template>
								<v-card width="346" class="rounded-lg border shadow-sm">
									<v-card-text class="pa-4">
										<v-row dense>
											<v-col cols="6" class="mb-4">
												<div class="text-caption text-grey mb-1">{{ $t('report.info_created_by') }}</div>
												<div class="text-body-2 font-weight-medium">
													{{ getNameById(report.createdBy) || $t('report.unknown_user') }}
												</div>
											</v-col>
											<v-col cols="6" class="mb-4">
												<div class="text-caption text-grey mb-1">{{ $t('report.info_created_at') }}</div>
												<div class="text-body-2 font-weight-medium">
													{{ formatDateTime(report.createdAt) }}
												</div>
											</v-col>
											<v-col cols="6">
												<div class="text-caption text-grey mb-1">{{ $t('report.info_decided_by') }}</div>
												<div class="text-body-2 font-weight-medium">
													{{ getNameById(report.decidedBy) || $t('report.not_available') }}
												</div>
											</v-col>
											<v-col cols="6">
												<div class="text-caption text-grey mb-1">{{ $t('report.info_decided_at') }}</div>
												<div class="text-body-2 font-weight-medium">
													{{ formatDateTime(report.decidedAt) || $t('report.not_available') }}
												</div>
											</v-col>
										</v-row>
									</v-card-text>
								</v-card>
							</v-menu>

							<v-tooltip v-if="isPreviewDuty" :text="$t('tooltips.report_disabled_preview')" open-delay="500" max-width="150">
								<template #activator="{ props }">
									<span v-bind="props">
										<v-btn
											icon="mdi-pencil"
											variant="text"
											size="small"
											color="grey-darken-1"
											class="mx-1"
											:disabled="true"
											@click="viewReport(report)"
										/>
									</span>
								</template>
							</v-tooltip>

							<v-btn
								v-else
								icon="mdi-pencil"
								variant="text"
								size="small"
								color="grey-darken-1"
								class="mx-1"
								:disabled="!canEdit"
								@click="viewReport(report)"
							/>
							<v-tooltip v-if="isPreviewDuty" :text="$t('tooltips.report_disabled_preview')" open-delay="500" max-width="150">
								<template #activator="{ props }">
									<span v-bind="props">
										<v-btn
											icon="mdi-delete"
											variant="text"
											size="small"
											color="grey-darken-1"
											:disabled="true"
											@click="deleteReport(report)"
										/>
									</span>
								</template>
							</v-tooltip>

							<v-btn
								v-else
								icon="mdi-delete"
								variant="text"
								size="small"
								color="grey-darken-1"
								:disabled="!canEdit"
								@click="deleteReport(report)"
							/>
						</div>
					</div>

					<v-row dense class="mb-4">
						<v-col cols="6">
							<div class="text-caption text-grey mb-1">{{ $t('report.label_start') }}</div>
							<div class="text-body-1 font-weight-medium">{{ formatDateTime(report.details.start) }}</div>
						</v-col>
						<v-col cols="6">
							<div class="text-caption text-grey mb-1">{{ $t('report.label_end') }}</div>
							<div class="text-body-1 font-weight-medium">{{ formatDateTime(report.details.end) }}</div>
						</v-col>
					</v-row>

					<v-row dense>
						<v-col cols="6">
							<div class="text-caption text-grey mb-1">{{ $t('report.label_reason') }}</div>
							<div class="text-body-2">{{ report.details.reason }}</div>
						</v-col>
						<v-col cols="6" v-if="report.comment">
							<div class="text-caption text-grey mb-1">{{ $t('absence.comment') }}</div>
							<div class="text-body-2">{{ report.comment }}</div>
						</v-col>
					</v-row>
				</div>
			</v-card>
		</div>

		<v-sheet v-else class="text-center pa-10 bg-transparent">
			<div class="text-grey">{{ $t('message_no_records_found') }}</div>
		</v-sheet>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { usePlanningBridgeStore } from '@/rennovation/planning-view/store/bridge/planningBridge';
import { useReportStore } from '@/rennovation/planning-view/store/useReportStore';
import { usePlanningStore } from '@/rennovation/planning-view/store/usePlanningStore';
// @ts-ignore – shared JS composable without type declarations
import useDateTime from '../../../../../../components/composables/useDateTime.js';
import useNameFormatter from '../../../../../../components/composables/useNameFormatter.js';
import ReportForm from '@/rennovation/planning-view/components/Layout/Modals/ReportForm.vue';
import AddCommentDialog from '../../../../../../components/common/AddCommentDialog.vue';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import { ExtraWorkResponse } from '@/rennovation/planning-view/types/api/extra-work-response';

const { t } = useI18n();
defineOptions({
	inheritAttrs: false,
});
const reportStore = useReportStore();
const planningStore = usePlanningStore();
const { formatDateTime } = useDateTime();
const { getNameById } = useNameFormatter();
const planningBridge = usePlanningBridgeStore();

// ── Dialog state ──────────────────────────────────────────
const showReport = ref(false);
const selectedReport = ref<ExtraWorkResponse>(null);
const formMode = ref<'create' | 'edit'>('create');

const showAddCommentDialog = ref(false);
const addCommentTitle = computed(() => t('report.confirm_delete_with_comment', { module: t('planning_extra_work').toLowerCase() }));

const deleteError = ref<string | null>(null);

// ── Computed ──────────────────────────────────────────────
const isPreviewDuty = computed(() => {
	const preview = selectedDuty.value?.preview;
	return preview !== null && preview !== undefined;
});
const hasReports = computed(() => reportStore.dutyReports.length > 0);
const selectedDuty = computed(() => reportStore.activeDuty);
const dutyDate = computed(() => (reportStore.activeDuty ? DateTime.fromISO(reportStore.activeDuty.dayObject.dateString + 'T00:00:00Z') : ''));
const isUnallocatedDuty = computed(() => planningStore.selectedDuty.user == null);
const canEdit = computed(() => {
	const date = dutyDate.value;
	if (!date) {
		return true;
	}
	return !planningBridge.isBeforeFirstPastDay(date.toISODate(), null);
});

// ── Lifecycle ─────────────────────────────────────────────
onMounted(async () => {
	await reportStore.setActiveDutyId(planningStore.selectedDuty.id);

	if (reportStore.activeDutyId) {
		reportStore.loadReportsByDuty(reportStore.activeDutyId);
	}
});

// ── Actions ───────────────────────────────────────────────
const handleCreate = () => {
	formMode.value = 'create';
	selectedReport.value = null;
	showReport.value = true;
};

const viewReport = (report: ExtraWorkResponse) => {
	formMode.value = 'edit';
	selectedReport.value = report;
	showReport.value = true;
};

const deleteReport = (report: ExtraWorkResponse) => {
	selectedReport.value = report;
	showAddCommentDialog.value = true;
};

const handleDeleteWithComment = async (comment?: string | null) => {
	try {
		await reportStore.deleteReport(selectedReport.value._id ?? selectedReport.value.id, comment?.trim() || '');
		showAddCommentDialog.value = false;
		if (reportStore.activeDutyId) {
			await reportStore.loadReportsByDuty(reportStore.activeDutyId);
		}
	} catch {
		deleteError.value = reportStore.fetchError ?? t('report.delete_failed');
	}
};

const cancelEdit = () => {
	showReport.value = false;
};

const handleSubmit = async () => {
	showReport.value = false;
	if (reportStore.activeDutyId) {
		await reportStore.loadReportsByDuty(reportStore.activeDutyId);
	}
};

// ── Helpers ───────────────────────────────────────────────
const getStatusClass = (status: string): string => {
	const s = status?.toLowerCase();
	if (s === 'approved') return 'status-approved';
	if (s === 'pending') return 'status-pending';
	if (s === 'rejected') return 'status-rejected';
	return '';
};

const formatStatus = (status: string): string => {
	if (!status) return '';
	const s = status.toLowerCase();

	if (s === 'approved') return t('report.status_approved');
	if (s === 'pending') return t('report.status_pending');
	if (s === 'rejected') return t('report.status_rejected');

	return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};
</script>

<style scoped>
.status-chip {
	display: inline-flex;
	align-items: center;
	height: 19px;
	padding: 0px 12px;
	border-radius: 20px;
	font-size: 0.7rem;
	border: 1px solid;
}

.status-chip.status-approved {
	background-color: #e8f5e9;
	border-color: #4caf50;
	color: #2e7d32;
}

.status-chip.status-pending {
	background-color: #eeeeee;
	border-color: #9e9e9e;
	color: #616161;
}

.status-chip.status-rejected {
	background-color: #fdecea;
	border-color: #f44336;
	color: #c62828;
}

.duty-reports-tab {
	max-height: 520px;
	overflow-y: auto;
}

.report-card-body {
	padding: 24px;
}

.shimmer-line {
	background: linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%);
	background-size: 200% 100%;
	animation: shimmer 1.4s infinite;
}

.shimmer-card {
	overflow: hidden;
}

.shimmer-title {
	width: 80px;
	height: 16px;
	border-radius: 4px;
}

.shimmer-badge {
	width: 64px;
	height: 20px;
	border-radius: 20px;
}

.shimmer-icon {
	width: 28px;
	height: 28px;
	border-radius: 50%;
}

.shimmer-label {
	width: 40px;
	height: 10px;
	border-radius: 4px;
}

.shimmer-date {
	width: 140px;
	height: 16px;
	border-radius: 4px;
}

.shimmer-reason-label {
	width: 50px;
	height: 10px;
	border-radius: 4px;
}

.shimmer-reason-text {
	width: 100%;
	height: 14px;
	border-radius: 4px;
}

@keyframes shimmer {
	0% {
		background-position: 200% 0;
	}
	100% {
		background-position: -200% 0;
	}
}
</style>
